// UPPDRAG-15 — admin UI for per-court sponsors. Mounted inside each
// court Card in AdminCourts.tsx as a collapsible sub-section.
//
// Design parity with the league-side <SponsorLogoUpload> (PR-16.3)
// where it makes sense (file-validation + bucket-key heuristics)
// but the data model differs:
//   - Court can have MULTIPLE sponsors (PR-16.3 was 1:1 my_series +
//     1:1 register variants)
//   - Each sponsor is a row in the new `court_sponsors` table
//   - Sponsor row carries optional click_url + active_from/active_to
//
// Sponsorrad layout:
//   [thumbnail] [name input] [click_url input]
//   [active_from] [active_to] [display_order]
//   [Byt logga] [Ta bort]
//
// "Lägg till sponsor" creates a new row with an empty logo_url; the
// admin must upload a file before the row becomes visible to players
// (logo_url is NOT NULL at the DB level, so we INSERT only after a
// successful storage upload).

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, X, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  validateSponsorImageFile,
  isOurLogosBucketUrl,
  extractLogosBucketKey,
} from "@/lib/sponsorUploadHelpers";
import { courtSponsorStoragePath, type CourtSponsorRow } from "@/lib/courtSponsorHelpers";

const ACCEPT_ATTR = "image/png,image/jpeg,image/webp";

export default function CourtSponsorsManager({ courtId }: { courtId: string }) {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ["court-sponsors", courtId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("court_sponsors" as any)
        .select("id, court_id, sponsor_name, logo_url, click_url, active_from, active_to, display_order")
        .eq("court_id", courtId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as CourtSponsorRow[];
    },
    enabled: expanded,
  });

  // Add a brand-new sponsor row. We INSERT only after the admin has
  // picked a file + the upload succeeded — logo_url is NOT NULL.
  const addSponsorMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const validation = validateSponsorImageFile(file);
      if (!validation.ok) {
        if (validation.error === "too_large")
          throw new Error(t("courtSponsors.errTooLarge", "Filen är större än 2 MiB."));
        if (validation.error === "wrong_type")
          throw new Error(t("courtSponsors.errWrongType", "Endast PNG, JPEG och WebP stöds."));
        throw new Error(t("courtSponsors.errNoFile", "Ingen fil vald."));
      }

      // Insert empty row first to get the sponsor id (used in the
      // storage path so multiple sponsors per court don't clobber).
      // logo_url has a NOT NULL constraint — use a placeholder that
      // we overwrite immediately after upload (within the same mutation).
      const { data: inserted, error: insertErr } = await supabase
        .from("court_sponsors" as any)
        .insert({
          court_id: courtId,
          logo_url: "pending",
          display_order: (sponsors.length ?? 0),
          created_by: user?.id ?? null,
        } as any)
        .select("id")
        .single();
      if (insertErr) throw insertErr;
      const sponsorId = (inserted as any).id as string;

      // Upload to storage
      const path = courtSponsorStoragePath(courtId, sponsorId, validation.ext);
      const { error: uploadErr } = await supabase.storage
        .from("logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadErr) {
        // Roll back the row so we don't leave a "pending" placeholder
        await supabase.from("court_sponsors" as any).delete().eq("id", sponsorId);
        throw uploadErr;
      }
      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
      const url = `${urlData.publicUrl}?v=${Date.now()}`;

      // Update logo_url on the row
      const { error: updateErr } = await supabase
        .from("court_sponsors" as any)
        .update({ logo_url: url })
        .eq("id", sponsorId);
      if (updateErr) throw updateErr;

      return { sponsorId, url };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["court-sponsors", courtId] });
      toast.success(t("courtSponsors.added", "Sponsor tillagd"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateSponsorMutation = useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<CourtSponsorRow, "sponsor_name" | "click_url" | "active_from" | "active_to" | "display_order">>;
    }) => {
      const { error } = await supabase
        .from("court_sponsors" as any)
        .update(patch as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["court-sponsors", courtId] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const replaceLogoMutation = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const validation = validateSponsorImageFile(file);
      if (!validation.ok) {
        if (validation.error === "too_large")
          throw new Error(t("courtSponsors.errTooLarge", "Filen är större än 2 MiB."));
        if (validation.error === "wrong_type")
          throw new Error(t("courtSponsors.errWrongType", "Endast PNG, JPEG och WebP stöds."));
        throw new Error(t("courtSponsors.errNoFile", "Ingen fil vald."));
      }
      const path = courtSponsorStoragePath(courtId, id, validation.ext);
      const { error: uploadErr } = await supabase.storage
        .from("logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
      const url = `${urlData.publicUrl}?v=${Date.now()}`;
      const { error } = await supabase
        .from("court_sponsors" as any)
        .update({ logo_url: url })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["court-sponsors", courtId] });
      toast.success(t("courtSponsors.logoReplaced", "Sponsorlogga uppdaterad"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteSponsorMutation = useMutation({
    mutationFn: async ({ id, logoUrl }: { id: string; logoUrl: string }) => {
      // Delete the row first (RLS-safe; storage cleanup is best-effort
      // since we don't have a transaction across DB + storage).
      const { error } = await supabase
        .from("court_sponsors" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
      // Best-effort storage cleanup
      if (isOurLogosBucketUrl(logoUrl)) {
        const key = extractLogosBucketKey(logoUrl);
        if (key) {
          await supabase.storage.from("logos").remove([key]).catch(() => {
            /* non-fatal — admin can clean up later */
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["court-sponsors", courtId] });
      toast.success(t("courtSponsors.deleted", "Sponsor borttagen"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="border-t pt-2 mt-2 space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-xs font-medium text-muted-foreground hover:text-foreground flex items-center gap-1"
      >
        {expanded ? "▾" : "▸"} {t("courtSponsors.title", "Sponsorer")}
        {sponsors.length > 0 && !expanded && (
          <span className="text-[10px] bg-primary/10 text-primary rounded px-1">
            {sponsors.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="space-y-3">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : sponsors.length === 0 ? (
            <p className="text-[11px] text-muted-foreground italic">
              {t(
                "courtSponsors.empty",
                "Inga sponsorer för denna bana ännu.",
              )}
            </p>
          ) : (
            <div className="space-y-3">
              {sponsors.map((s) => (
                <SponsorRow
                  key={s.id}
                  sponsor={s}
                  onUpdate={(patch) => updateSponsorMutation.mutate({ id: s.id, patch })}
                  onReplaceLogo={(file) => replaceLogoMutation.mutate({ id: s.id, file })}
                  onDelete={() => deleteSponsorMutation.mutate({ id: s.id, logoUrl: s.logo_url })}
                  busy={replaceLogoMutation.isPending || deleteSponsorMutation.isPending}
                />
              ))}
            </div>
          )}

          <AddSponsorButton
            onPick={(file) => addSponsorMutation.mutate({ file })}
            busy={addSponsorMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}

// ─── Single sponsor row ─────────────────────────────────────────

function SponsorRow({
  sponsor,
  onUpdate,
  onReplaceLogo,
  onDelete,
  busy,
}: {
  sponsor: CourtSponsorRow;
  onUpdate: (patch: Partial<CourtSponsorRow>) => void;
  onReplaceLogo: (file: File) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);
  // Local form state with debounced commit on blur (matches AdminCourts pattern).
  const [name, setName] = useState(sponsor.sponsor_name ?? "");
  const [clickUrl, setClickUrl] = useState(sponsor.click_url ?? "");
  const [activeFrom, setActiveFrom] = useState(sponsor.active_from ?? "");
  const [activeTo, setActiveTo] = useState(sponsor.active_to ?? "");

  return (
    <div className="rounded border p-2 space-y-2 bg-muted/20">
      <div className="flex items-start gap-2">
        {/* Thumbnail */}
        <div className="w-16 h-12 shrink-0 border rounded bg-card flex items-center justify-center overflow-hidden">
          {sponsor.logo_url && sponsor.logo_url !== "pending" ? (
            <img src={sponsor.logo_url} alt={sponsor.sponsor_name ?? "Sponsor"} className="max-w-full max-h-full object-contain" />
          ) : (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Name + click_url */}
        <div className="flex-1 space-y-1 min-w-0">
          <Input
            className="h-7 text-xs"
            placeholder={t("courtSponsors.namePlaceholder", "Sponsorsnamn (valfritt)")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              if ((sponsor.sponsor_name ?? "") !== name) {
                onUpdate({ sponsor_name: name || null });
              }
            }}
          />
          <Input
            className="h-7 text-xs"
            placeholder={t("courtSponsors.clickUrlPlaceholder", "https://… (valfri klicklänk)")}
            value={clickUrl}
            onChange={(e) => setClickUrl(e.target.value)}
            onBlur={() => {
              if ((sponsor.click_url ?? "") !== clickUrl) {
                onUpdate({ click_url: clickUrl || null });
              }
            }}
          />
        </div>
      </div>

      {/* Active window */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-muted-foreground">
          {t("courtSponsors.activeWindow", "Aktiv period (valfri)")}:
        </span>
        <Input
          type="date"
          className="h-7 text-xs w-auto"
          value={activeFrom}
          onChange={(e) => setActiveFrom(e.target.value)}
          onBlur={() => {
            if ((sponsor.active_from ?? "") !== activeFrom) {
              onUpdate({ active_from: activeFrom || null });
            }
          }}
        />
        <span className="text-[10px] text-muted-foreground">→</span>
        <Input
          type="date"
          className="h-7 text-xs w-auto"
          value={activeTo}
          onChange={(e) => setActiveTo(e.target.value)}
          onBlur={() => {
            if ((sponsor.active_to ?? "") !== activeTo) {
              onUpdate({ active_to: activeTo || null });
            }
          }}
        />
      </div>

      {/* Replace + Delete */}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onReplaceLogo(f);
            if (inputRef.current) inputRef.current.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          <Upload className="h-3.5 w-3.5" />
          {t("courtSponsors.replaceLogo", "Byt logga")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 text-destructive hover:text-destructive ml-auto"
          onClick={onDelete}
          disabled={busy}
        >
          <X className="h-3.5 w-3.5" />
          {t("courtSponsors.delete", "Ta bort")}
        </Button>
      </div>
    </div>
  );
}

// ─── Add-sponsor button ─────────────────────────────────────────

function AddSponsorButton({
  onPick,
  busy,
}: {
  onPick: (file: File) => void;
  busy: boolean;
}) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        {t("courtSponsors.add", "Lägg till sponsor")}
      </Button>
    </>
  );
}

// Make Label a no-op import for now (we don't use it explicitly above —
// admin labels are the small-text spans). Keeping the import means
// TS doesn't complain if a future revision adds an explicit <Label>.
void Label;
